/**
 * DropEngagementStrip — page composition of the marquee + secondary tiles.
 *
 * This is the strip that lives below the live player on a Drop page (and
 * in the right rail when the creator is offline). One marquee sponsor +
 * up to N compact secondary tiles. Anything beyond N becomes a "View all
 * engagements" link.
 *
 * The strip itself is a thin composer — it doesn't know what mechanic any
 * sponsor is running. Callers pass in already-built React nodes for the
 * marquee body and each tile body. The mechanic library
 * (MechanicJourney / MechanicLeaderboard / MechanicBingo / MechanicBoss /
 * MechanicFlash / MechanicStreak) renders those nodes.
 *
 * Layout
 *   - "stream-below": single-row horizontal (used on the live state) —
 *     marquee takes ~60-70% width, secondaries flow to the right.
 *   - "right-rail": vertical column (used on the offline state) —
 *     marquee stacks above a tile grid.
 */

import React from "react";
import {MechanicSlot, type MechanicBrandChrome} from "./MechanicSlot";
import "./DropEngagementStrip.scss";

export type DropStripLayout = "stream-below" | "right-rail";

export interface DropStripMarquee {
    chrome: MechanicBrandChrome;
    /** The mechanic body — built by the caller (MechanicJourney, etc.). */
    body: React.ReactNode;
}

export interface DropStripTile {
    chrome: MechanicBrandChrome;
    /** Optional inline mechanic glimpse (mini ring, mini bar, mini node strip). */
    body?: React.ReactNode;
    /** Type label rendered after the body (e.g. "Boss · 38%", "Day 5 of 7"). */
    typeLabel?: string;
}

interface DropEngagementStripProps {
    layout: DropStripLayout;
    /** The one prominent sponsor in the marquee position. */
    marquee: DropStripMarquee;
    /** Compact tiles for the remaining active sponsors. */
    secondaries: DropStripTile[];
    /**
     * Hard cap on visible tiles. When `secondaries.length` exceeds this,
     *  excess collapses to a "+N more" link. Defaults to 2.
     */
    maxTiles?: number;
    /** Href for the "+N more · View all" link. */
    viewAllHref?: string;
}

export const DropEngagementStrip: React.FC<DropEngagementStripProps> = ({
    layout, marquee, secondaries, maxTiles = 2, viewAllHref = "#",
}) => {
    const visibleTiles = secondaries.slice(0, maxTiles);
    const overflow = Math.max(0, secondaries.length - maxTiles);

    return (
        <div className={`drop-strip drop-strip--${layout}`} role="group" aria-label="Active engagement strip">
            <div className="drop-strip__marquee">
                <MechanicSlot variant="marquee" {...marquee.chrome}>
                    {marquee.body}
                </MechanicSlot>
            </div>

            {(visibleTiles.length > 0 || overflow > 0) &&
                <aside className="drop-strip__tiles" aria-label="Other sponsors">
                    {layout === "right-rail" &&
                        <div className="drop-strip__tiles-lbl">
                            Other sponsors · <strong>{secondaries.length} active</strong>
                        </div>
                    }
                    <div className="drop-strip__tiles-grid">
                        {visibleTiles.map((tile, i) =>
                            <MechanicSlot
                                key={`${tile.chrome.brandKey}-${i}`}
                                variant="tile"
                                {...tile.chrome}
                                mechanicTypeLabel={tile.typeLabel}
                            >
                                {tile.body ?? <span />}
                            </MechanicSlot>)}
                    </div>
                    {overflow > 0 &&
                        <a className="drop-strip__more" href={viewAllHref}>
                            + {overflow} more · View all
                        </a>
                    }
                </aside>
            }
        </div>
    );
};
